import React from 'react';
import { useFormState } from 'react-final-form';
import { Button } from '@tcl-boron-prefabs/button';
import {
  neuroFinancialAssistanceFields,
  getNeuroExtenuatingCircumstancesDropdownOptions,
  getNeuroNumberFamilyMembersDropdownOptions,
  IS_LEGALLY_AUTHORIZED_REPRESENTATIVE_OPTIONS,
  NeuroFinancialAssistanceContent,
  TEXT_FIELD_CHARACTER_LIMIT,
} from '@tempus/patient-forms-service-shared';
import { FormProps } from '../../forms';
import FormAlert, { AlertStatus } from '../../shared/FormAlert';
import FormBannerError from '../../shared/FormBannerError';
import FormTable from '../../shared/FormTable';
import { CHARACTER_LIMIT_WARNING, REQUIRED_FIELD_WARNING } from '../../shared/validation-utils';
import styles from '../../shared/styles.module.css';
import { Typography } from '@mui/material';
import UploadZone, { CachedFile } from '../../../../portal/components/UploadZone';
import getBase64File from '../../../../portal/utils/getBase64File';
import { OPTION } from '../../../../portal/shared/constants';
import { combineFormConfigs } from '../../shared/utils';
import { EXCLUDED_FORM_FIELDS } from '../../shared/PatientStandaloneFormAuthenticationFlow';

export const neuroFinancialAssistanceFieldsFrontend = [
  {
    name: 'grossAnnualHouseholdIncome',
    props: {
      type: 'number',
    },
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
  },
  {
    name: 'numberOfFamilyMembersInHousehold',
    props: {
      dropdownOptions: getNeuroNumberFamilyMembersDropdownOptions(),
    },
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
  },
  {
    name: 'extenuatingCircumstances',
    props: {
      checkboxOptions: getNeuroExtenuatingCircumstancesDropdownOptions(),
    },
  },
  {
    name: 'isLegallyAuthorizedRepresentative',
    props: {
      placeholder: 'Select',
      dropdownOptions: IS_LEGALLY_AUTHORIZED_REPRESENTATIVE_OPTIONS,
    },
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: OPTION.YES,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'legallyAuthorizedRepresentativeFirstName',
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: OPTION.YES,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'legallyAuthorizedRepresentativeLastName',
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: OPTION.YES,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'legallyAuthorizedRepresentativeRelationshipToPatient',
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: OPTION.YES,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'legallyAuthorizedRepresentativeEmail',
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: OPTION.YES,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'legallyAuthorizedRepresentativePhoneNumber',
    validate: (value: any | undefined): string | undefined => {
      if (!value) {
        return REQUIRED_FIELD_WARNING;
      } else if (String(value).length > TEXT_FIELD_CHARACTER_LIMIT) {
        return CHARACTER_LIMIT_WARNING;
      }
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: OPTION.YES,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'representativeSignature',
    props: { placeholder: 'Sign here' },
    validate: (value: any | undefined) => {
      if (!value) return REQUIRED_FIELD_WARNING;
    },
  },
  {
    conditionalValueConfig: {
      fieldNameToCheck: 'isLegallyAuthorizedRepresentative',
      valueToCheck: OPTION.NO,
      conditionalLogic: (value: any, conditionalValue: string) => {
        return value === conditionalValue;
      },
    },
    name: 'signature',
    props: { placeholder: 'Sign here' },
    validate: (value: any | undefined) => {
      if (!value) return REQUIRED_FIELD_WARNING;
    },
  },
];

type NeuroFinancialAssistanceProps = FormProps & {
  cachedFile?: CachedFile;
  setCachedFile: (file: CachedFile | undefined) => void;
};

const NeuroFinancialAssistanceForm: React.FC<NeuroFinancialAssistanceProps> = (props) => {
  const { cachedFile, setCachedFile } = props;
  const formState = useFormState();
  const formConfig = combineFormConfigs(neuroFinancialAssistanceFieldsFrontend, neuroFinancialAssistanceFields).filter(
    (field) => !EXCLUDED_FORM_FIELDS.includes(field.name),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
      {formState.submitFailed ? <FormBannerError /> : undefined}
      <NeuroFinancialAssistanceContent />
      <FormTable fields={formConfig} />
      <Typography variant="h3">
        Acceptable proof of household income
        <Typography variant="body2" color="textSecondary">
          Upload your most recent Form 1040, W-2, or pay stub to verify your income.
        </Typography>
      </Typography>
      <UploadZone
        docPreview={cachedFile?.content}
        docType={cachedFile?.type}
        setFile={async (file) => {
          if (file) {
            const base64File = (await getBase64File(file)) as string;
            setCachedFile({
              name: file.name,
              type: file.type,
              content: base64File,
            } as CachedFile);
          } else {
            setCachedFile(undefined);
          }
        }}
        width="100%"
      />
      {cachedFile?.content ? (
        <Button buttonType="tertiary" small onClick={() => setCachedFile(undefined)} ariaLabel="Remove">
          Remove
        </Button>
      ) : null}
      <div className={styles.footer}>
        {props.handleNavigationBack && (
          <div className={styles.backButton}>
            <Button
              ariaLabel="neuro-financial-assistance-back"
              type="button"
              buttonType="secondary"
              onClick={props.handleNavigationBack}
              disabled={formState.submitting}
            >
              Back
            </Button>
          </div>
        )}
        {props.showAlert && (
          <FormAlert
            setShowAlert={props.setShowAlert}
            message={''}
            title={'Successfully submitted your Financial Assistance. Thank you!'}
            status={AlertStatus.SUCCESS}
          />
        )}
        <Button
          loading={formState.submitting}
          ariaLabel="neuro-financial-assistance-submit"
          type="submit"
          disabled={!formState.valid || !cachedFile?.content || formState.submitting}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default NeuroFinancialAssistanceForm;
